import React from "react";
import { Alert, Button, Skeleton, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { deleteDrinkActivity } from "../../../services/drinks/DrinkService";
import { outbox } from "../../../offline";
import { buildRecentTemplateKey, getQuickDrinkName } from "../../../services/drinkService";
import { DEFAULT_DRINK_DURATION_MINUTES } from "../drinkStats/constants";
import { useAddDrink } from "./useAddDrink";
import { useAppFeedback } from "../../useAppFeedback";

type AddDrinkState = ReturnType<typeof useAddDrink>;

export const QuickAddList: React.FC<{ state: AddDrinkState }> = ({ state }) => {
    const { message } = useAppFeedback();
    const {
        user, t, recentTemplates, recentTemplatesLoading, recentTemplatesError, drinks, isOnline, outboxEntries,
        setLastAddedDrink, recentActivities, reloadRecentActivities,
    } = state;
    const failedEntries = React.useMemo(() => (outboxEntries ?? []).filter((entry) => entry.status === "failed" && entry.payload.user.userid === user?.userId), [outboxEntries, user?.userId]);
    // Taps that are not in the loaded activities yet (the outbox flushes after the undo window).
    const [pendingTaps, setPendingTaps] = React.useState<Record<string, number>>({});
    // The drink tapped last moves to the front right away; synced activities already arrive newest first.
    const [lastTappedKey, setLastTappedKey] = React.useState<string>();
    React.useEffect(() => { setPendingTaps({}); setLastTappedKey(undefined); }, [recentActivities]);
    const orderedTemplates = React.useMemo(() => lastTappedKey
        ? [...recentTemplates].sort((a, b) => Number(b.templateKey === lastTappedKey) - Number(a.templateKey === lastTappedKey))
        : recentTemplates, [recentTemplates, lastTappedKey]);
    const bump = (key: string, delta: number) => setPendingTaps((current) => ({ ...current, [key]: Math.max(0, (current[key] ?? 0) + delta) }));
    return (
        <section className="add-drink-quick-section" aria-label={t("addDrink.quickAdd.title")}>
            {failedEntries.map((entry) => (
                <Alert
                    key={entry.id}
                    type="error"
                    showIcon
                    message={t("addDrink.offline.failedDrink", { drink: entry.payload.drink.drinkname })}
                    action={<>
                        <Button style={{ minHeight: 44 }} onClick={() => { void outbox.retry(entry.id); }}>{t("addDrink.offline.retry")}</Button>
                        <Button style={{ minHeight: 44 }} onClick={() => { void outbox.remove(entry.id); }}>{t("addDrink.offline.remove")}</Button>
                    </>}
                />
            ))}
            {recentTemplatesLoading && recentTemplates.length === 0 && (
                <output className="add-drink-quick-row add-drink-quick-loading" aria-label={t("addDrink.quickAdd.loading")} aria-busy="true">
                    {[0, 1].map((item) => <Skeleton.Button active block key={item} />)}
                </output>
            )}
            {recentTemplates.length > 0 && (
                <div className="add-drink-quick-row">
                            {orderedTemplates.map((template) => {
                                const drinkName = getQuickDrinkName(template.drinkName);
                                let durationLabel = "";
                                if (template.durationMinutes === 0) {
                                    durationLabel = t("addDrink.quickAdd.durationChug");
                                } else if (template.durationMinutes != null) {
                                    durationLabel = t("addDrink.quickAdd.durationApprox", { minutes: template.durationMinutes });
                                }
                                const loggedCount = template.count + (pendingTaps[template.templateKey] ?? 0);
                                const buttonLabel = t("addDrink.quickAdd.buttonLabel", {
                                    drink: drinkName,
                                    amount: template.amountMl,
                                    count: loggedCount,
                                    duration: durationLabel,
                                });
                                return (
                                    <div className="add-drink-quick-tile" key={template.templateKey}>
                                        <Button
                                            type="primary"
                                            className="add-drink-quick-add"
                                            aria-label={buttonLabel}
                                            onClick={async () => {
                                                if (!user) {
                                                    message.error(t("addDrink.messages.loginFirst"));
                                                    return;
                                                }
                                                const baseDrink = drinks.find((item) => item.id === template.drinkId);
                                                if (!baseDrink) {
                                                    message.warning(
                                                        t("addDrink.quickAdd.unavailable", {
                                                            drinkName: template.drinkName,
                                                        })
                                                    );
                                                    return;
                                                }
                                                try {
                                                    const fallbackDurationMinutes = DEFAULT_DRINK_DURATION_MINUTES;
                                                    const templateDurationMinutes =
                                                        template.durationMinutes ?? fallbackDurationMinutes;
                                                    const now = new Date();
                                                    const startedAt =
                                                        templateDurationMinutes > 0
                                                            ? new Date(now.getTime() - templateDurationMinutes * 60 * 1000)
                                                            : now;
                                                    const entry = await outbox.enqueue({
                                                        drink: {
                                                            drinkid: baseDrink.id,
                                                            drinkname: baseDrink.name,
                                                            promille: baseDrink.promille,
                                                        },
                                                        amount: template.amountMl,
                                                        tsInsert: now.getTime(),
                                                        tsStart: startedAt.getTime(),
                                                        user: { userid: user.userId, username: user.username },
                                                    });
                                                    bump(template.templateKey, 1);
                                                    setLastTappedKey(template.templateKey);
                                                    const undo = async () => {
                                                        try {
                                                            const queued = await outbox.list();
                                                            if (!queued.some((queuedEntry) => queuedEntry.id === entry.id)) {
                                                                message.warning(t("addDrink.quickAdd.alreadySynced", { drinkName: template.drinkName }));
                                                                reloadRecentActivities();
                                                                return;
                                                            }
                                                            await outbox.remove(entry.id);
                                                            bump(template.templateKey, -1);
                                                            window.clearTimeout(flushTimer);
                                                            setLastAddedDrink(null);
                                                            message.success(t("addDrink.quickAdd.removed", { drinkName: template.drinkName }));
                                                        } catch {
                                                            message.error(t("addDrink.errors.removeDrink"));
                                                        }
                                                    };
                                                    message.success({
                                                        content: (
                                                            <span>
                                                                {isOnline
                                                                    ? t("addDrink.quickAdd.added", { drinkName: template.drinkName })
                                                                    : t("addDrink.quickAdd.addedQueued", { drinkName: template.drinkName })}
                                                                <Button type="link" onClick={() => void undo()}>{t("addDrink.quickAdd.undo")}</Button>
                                                            </span>
                                                        ),
                                                        duration: 5.5,
                                                    });
                                                    setLastAddedDrink({ name: template.drinkName, amount: template.amountMl });
                                                    const flushTimer = window.setTimeout(() => { void outbox.flush(); }, 5500);
                                                } catch {
                                                    message.error(t("addDrink.errors.addDrink"));
                                                }
                                            }}
                                        >
                                            <span className="add-drink-quick-name">{drinkName}</span>
                                            <span className="add-drink-quick-amount">{template.amountMl} ml</span>
                                            {loggedCount > 0 && <span className="add-drink-quick-count" aria-hidden="true">×{loggedCount}</span>}
                                            <PlusOutlined aria-hidden="true" className="add-drink-quick-icon" />
                                        </Button>
                                        <Button
                                            type="text"
                                            className="add-drink-quick-remove"
                                            aria-label={t("addDrink.quickAdd.removeButton", { drink: template.drinkName, amount: template.amountMl })}
                                            title={t("addDrink.quickAdd.removeButton", { drink: template.drinkName, amount: template.amountMl })}
                                            disabled={!isOnline}
                                            aria-description={!isOnline ? t("addDrink.quickAdd.removeOffline") : undefined}
                                            onClick={async () => {
                                                if (!user) {
                                                    message.error(t("addDrink.messages.loginFirst"));
                                                    return;
                                                }
                                                const latest = recentActivities
                                                    .filter(
                                                        (activity) =>
                                                            activity.activityId &&
                                                            typeof activity.drinkId === "number" &&
                                                            activity.drinkId === template.drinkId &&
                                                            buildRecentTemplateKey(activity.drinkId, activity.amountMl) ===
                                                                template.templateKey
                                                    )
                                                    .sort(
                                                        (a, b) => (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0)
                                                    )[0];
                                                if (!latest?.activityId) {
                                                    message.warning(
                                                        t("addDrink.quickAdd.noRecent", {
                                                            drinkName: template.drinkName,
                                                        })
                                                    );
                                                    return;
                                                }
                                                try {
                                                    await deleteDrinkActivity(user, latest.activityId);
                                                    message.success(
                                                        t("addDrink.quickAdd.removed", {
                                                            drinkName: template.drinkName,
                                                        })
                                                    );
                                                    await reloadRecentActivities();
                                                } catch {
                                                    message.error(t("addDrink.errors.removeDrink"));
                                                }
                                            }}
                                        >
                                            <DeleteOutlined aria-hidden="true" />
                                        </Button>
                                    </div>
                                );
                            })}
                </div>
            )}
            {!recentTemplatesLoading && !recentTemplatesError && recentTemplates.length === 0 && (
                <Typography.Text className="add-drink-quick-empty" type="secondary">{t("addDrink.quickAdd.empty")}</Typography.Text>
            )}
        </section>
    );
};
